import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import { useTranslation } from 'react-i18next';
import { 
  MagnifyingGlassIcon, 
  CalendarIcon, 
  BuildingStorefrontIcon, 
  UserIcon,
  MapPinIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  ClockIcon, 
  PhotoIcon,
  TrashIcon,
  GlobeAltIcon,
  CheckBadgeIcon,
  NoSymbolIcon,
  HeartIcon,
  CalendarDaysIcon,
  ShieldCheckIcon,
  XMarkIcon,
  ChevronDownIcon,
  StarIcon,
  UserGroupIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/solid';
import { summarizeBusinessHours, perDayBusinessHours } from '../../utils/HoursFormat';
import LocationMapPicker from '../map/LocationMapPicker';

import { getCategoryColor } from '../../utils/categoryColors';
import { createFormatEventDate } from '../../utils/formatEventDate';

// --- HELPERS ---
const normalizeKey = (key) => key ? key.toLowerCase().replace(/\s+/g, '-') : '';

const ProviderManager = () => {
  const { t, i18n } = useTranslation();
  const formatEventDate = createFormatEventDate(i18n.language);
  const [activeTab, setActiveTab] = useState('providers');
  const [providers, setProviders] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingEvent, setEditingEvent] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '', description: '', start: '', end: '', category: 'Sale',
    recurrenceType: 'none', recurrenceEndDate: '',
    locationUseProvider: true, locationAddress: '', locationName: '', locationLat: '', locationLng: ''
  });
  const [editEndUserModified, setEditEndUserModified] = useState(false);
  const [editEndError, setEditEndError] = useState('');
  const [editShowMapPicker, setEditShowMapPicker] = useState(false);
  const [editSelectedImage, setEditSelectedImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);

  const toDateTimeLocal = (date) => {
    const d = new Date(date);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };

  // Locale & Date Formatting

  useEffect(() => {
    if (activeTab === 'providers') fetchProviders();
    else if (activeTab === 'events') fetchEvents();
    else if (activeTab === 'users') fetchUsers();
  }, [activeTab, viewMode]);

  // --- FETCHERS ---
  const fetchProviders = async () => {
    setLoading(true);
    try {
      const endpoint = viewMode === 'pending' ? '/admin/providers/pending' : '/map/providers';
      const res = await api.get(endpoint);
      setProviders(Array.isArray(res.data) ? res.data : []);
    } catch (err) { setProviders([]); } finally { setLoading(false); }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const query = viewMode === 'pending' ? '?status=pending' : '?status=approved';
      const res = await api.get(`/admin/events${query}`);
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch (err) { setEvents([]); } finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/users');
      const data = res.data;
      setUsers(Array.isArray(data) ? data : (data.users || []));
    } catch (err) { setUsers([]); } finally { setLoading(false); }
  };

  // --- ACTIONS ---
  const handleApprove = async (id) => {
    try { await api.put(`/admin/providers/${id}/approve`); fetchProviders(); } 
    catch (err) { alert(t('admin.alerts.approveError')); }
  };

  const handleReject = async (id) => {
    if (!window.confirm(t('alerts.confirmReject'))) return;
    try { await api.put(`/admin/providers/${id}/reject`); fetchProviders(); } 
    catch (err) { alert(t('admin.alerts.confirmRejectListing')); }
  };

  const handleDeleteProvider = async (id) => {
    if (!window.confirm(t('admin.alerts.confirmDeleteListing'))) return;
    try { await api.delete(`/map/listing/${id}`); fetchProviders(); } 
    catch (err) { alert(t('admin.alerts.deleteError')); }
  };

  const handleApproveEvent = async (id) => {
    try { await api.patch(`/admin/events/${id}/approve`); fetchEvents(); } 
    catch (err) { alert(t('admin.alerts.eventApproveError')); }
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm(t('admin.alerts.confirmDeleteEvent'))) return;
    try { await api.delete(`/admin/events/${id}`); fetchEvents(); } 
    catch (err) { alert(t('admin.alerts.deleteError')); }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm(t('admin.alerts.confirmDeleteUser'))) return;
    try { await api.delete(`/admin/users/${id}`); fetchUsers(); }
    catch (err) { alert(t('admin.alerts.deleteUserError')); }
  };

  const handleEditEventClick = (event) => {
    setEditingEvent(event);
    setEditFormData({
      title: event.title || '',
      description: event.description || '',
      start: event.start ? toDateTimeLocal(event.start) : '',
      end: event.end ? toDateTimeLocal(event.end) : '',
      category: event.category || 'Sale',
      recurrenceType: event.recurrence?.type || 'none',
      recurrenceEndDate: event.recurrence?.endDate ? toDateTimeLocal(event.recurrence.endDate).slice(0, 10) : '',
      locationUseProvider: event.location?.useProvider !== false,
      locationAddress: event.location?.address || '',
      locationName: event.location?.name || '',
      locationLat: event.location?.lat || '',
      locationLng: event.location?.lng || '',
    });
    setEditImagePreview(event.photoUrl || null);
    setEditSelectedImage(null);
    setEditEndUserModified(true);
    setEditEndError('');
    if (event.location?.lat && event.location?.lng) setEditShowMapPicker(true);
    else setEditShowMapPicker(false);
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditSelectedImage(file);
        setEditImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditEventSubmit = async (ev) => {
    ev.preventDefault();
    if (editFormData.end && editFormData.start && new Date(editFormData.end) <= new Date(editFormData.start)) {
      setEditEndError(t('events.event-management.alerts.end-before-start') || 'End time must be after start time.');
      return;
    }
    setEditEndError('');
    const data = new FormData();
    data.append('title', editFormData.title);
    data.append('description', editFormData.description);
    data.append('start', new Date(editFormData.start).toISOString());
    data.append('end', new Date(editFormData.end).toISOString());
    data.append('category', editFormData.category);
    data.append('recurrenceType', editFormData.recurrenceType);
    if (editFormData.recurrenceEndDate) data.append('recurrenceEndDate', editFormData.recurrenceEndDate);
    data.append('locationUseProvider', String(editFormData.locationUseProvider));
    data.append('locationAddress', editFormData.locationAddress);
    data.append('locationName', editFormData.locationName);
    if (editFormData.locationLat) data.append('locationLat', editFormData.locationLat);
    if (editFormData.locationLng) data.append('locationLng', editFormData.locationLng);
    if (editSelectedImage) data.append('image', editSelectedImage);
    try {
      await api.put(`/events/${editingEvent._id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEditingEvent(null);
      fetchEvents();
    } catch (err) {
      alert(t('admin.alerts.deleteError'));
    }
  };
  
  const handleToggleStar = async (eventId, currentStatus) => {
    const nextStatus = !currentStatus;
    setEvents(prevEvents => 
      prevEvents.map(ev => 
        ev._id === eventId ? { ...ev, isStarred: nextStatus } : ev
      )
    );
  
    try {
      const response = await api.patch(`/admin/events/${eventId}/star`, {
        isStarred: nextStatus
      });
  
      setEvents(prevEvents => 
        prevEvents.map(ev => 
          ev._id === eventId ? response.data : ev
        )
      );
    } catch (error) {
      console.error('Save failed:', error);
      setEvents(prevEvents => 
        prevEvents.map(ev => 
          ev._id === eventId ? { ...ev, isStarred: currentStatus } : ev
        )
      );
      alert("Could not save starred status to database.");
    }
  };

  // --- SUB-COMPONENT ---
  function HoursPreview({ summary, perDay }) {
    const [open, setOpen] = useState(false);
    return (
      <div className="text-sm">
        <button onClick={(e) => { e.preventDefault(); setOpen(!open); }} className="text-emerald-600 font-medium flex items-center gap-1 hover:text-emerald-700">
          {summary} <span className="text-[10px]">{open ? '▲' : '▼'}</span>
        </button>
        {open && perDay && (
          <div className="mt-2 p-2 rounded-lg bg-slate-50 border border-gray-100 shadow-inner">
            {perDay.map(({ day, label }) => (
              <div key={day} className="flex justify-between text-[11px] py-1 border-b border-gray-100 last:border-0">
                <span className="font-bold text-gray-400 uppercase">{t(`map.days.${day.toLowerCase()}`)}</span>

                <span className="text-gray-700">{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // --- CSV EXPORT ---
  const exportProvidersCSV = () => {
    const escapeCsv = (val) => {
      const s = String(val ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ['Business Name', 'Category', 'Status', 'Email', 'Phone', 'Address', 'Website', 'Description', 'Owner Email', 'Owner Name', 'Events', 'Event Count', 'Created Date'];
    const rows = providers.map(p => {
      const eventList = (p.events || []).map(e => `${e.title} [${e.status || 'unknown'}]`).join('; ');
      return [
        p.businessName,
        p.tier,
        p.contractStatus,
        p.contact?.email || '',
        p.contact?.phone || '',
        p.location?.address || '',
        p.details?.website || '',
        p.details?.description || '',
        p.user?.email || '',
        p.user?.name || '',
        eventList,
        (p.events || []).length,
        p.createdAt ? new Date(p.createdAt).toISOString().slice(0, 10) : '',
      ].map(escapeCsv).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `providers-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- FILTER LOGIC ---
  const lowerQ = searchQuery.toLowerCase();
  const filteredList = activeTab === 'providers' 
    ? providers.filter(p => (p.businessName || '').toLowerCase().includes(lowerQ))
    : activeTab === 'events' 
    ? events.filter(e => (e.title || '').toLowerCase().includes(lowerQ))
    : users.filter(u => (u.name || u.email || '').toLowerCase().includes(lowerQ));

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* HEADER & TABS */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('admin.provider-manager.title')}</h1>
        <div className="flex border-b border-gray-200 mb-6 overflow-x-auto whitespace-nowrap">
          <button onClick={() => setActiveTab('providers')} className={`pb-3 px-6 text-lg font-bold flex items-center gap-2 ${activeTab === 'providers' ? 'border-b-4 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            <BuildingStorefrontIcon className="h-6 w-6" /> {t('admin.tabs.providers')}
          </button>
          <button onClick={() => setActiveTab('events')} className={`pb-3 px-6 text-lg font-bold flex items-center gap-2 ${activeTab === 'events' ? 'border-b-4 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            <CalendarIcon className="h-6 w-6" /> {t('admin.tabs.events')}
          </button>
          <button onClick={() => setActiveTab('users')} className={`pb-3 px-6 text-lg font-bold flex items-center gap-2 ${activeTab === 'users' ? 'border-b-4 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            <UserIcon className="h-6 w-6" /> {t('admin.tabs.users')}
          </button>
        </div>

        {/* SEARCH & FILTERS */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-gray-50 p-4 rounded-xl">
          {(activeTab === 'providers' || activeTab === 'events') && (
            <div className="flex bg-white p-1 rounded-lg border border-gray-200">
              <button onClick={() => setViewMode('pending')} className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${viewMode === 'pending' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500'}`}>{t('admin.filter.pending')}</button>
              <button onClick={() => setViewMode('all')} className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${viewMode === 'all' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500'}`}>{t('admin.filter.all')}</button>
            </div>
          )}
          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input type="text" placeholder={t('admin.search-placeholder')} className="pl-9 w-full border border-gray-300 rounded-lg py-2 outline-none focus:ring-2 focus:ring-blue-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
            {activeTab === 'providers' && (
              <button
                onClick={exportProvidersCSV}
                title="Export CSV"
                className="p-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d5cab]"
              >
                <ArrowDownTrayIcon className="h-4 w-4 text-gray-600" />
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          
          {/* PROVIDERS TAB */}
          {activeTab === 'providers' && filteredList.map(p => {
            const bh = p.businessHours || null;
            const summary = bh ? summarizeBusinessHours(bh, t) : null;
            const perDay = bh ? perDayBusinessHours(bh, t) : null;
            return (
              <div key={p._id} className="bg-white overflow-hidden rounded-xl shadow-sm border border-gray-200 flex flex-col hover:shadow-md transition-shadow">
                <div className="h-44 w-full bg-gray-50 relative border-b border-gray-100">
                  {p.photoUrl ? <img src={p.photoUrl} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><PhotoIcon className="h-12 w-12 opacity-20" /></div>}
                  <div className="absolute top-3 right-3">
                    <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded shadow-sm border ${p.contractStatus === 'Pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-green-50 text-green-700 border-green-200'}`}>{t(`admin.status.${p.contractStatus.toLowerCase()}`)}</span>
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-bold text-lg text-gray-900 leading-tight mb-1">{p.businessName}</h3>
       
                  {p.user ? (
                    <div className="mb-4 p-2 rounded-lg bg-indigo-50/50 border border-indigo-100/50">
                      <div className="flex items-center gap-2 mb-1">
                        <UserIcon className="h-3 w-3 text-indigo-500" />
                        <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider">
                          {t('admin.providers.owner-account')}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-indigo-900 truncate">
                          {p.user.email}
                        </span>
                        <span className="text-[10px] text-indigo-600/70">
                          {p.user.name || t('admin.no-name')}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4 p-2 rounded-lg bg-amber-50/50 border border-amber-200/50">
                      <div className="flex items-center gap-2 mb-1">
                        <UserGroupIcon className="h-3 w-3 text-amber-600" />
                        <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider">
                          {t('admin.providers.community-suggestion')}
                        </span>
                      </div>
                      {(p.contact?.email || p.contact?.phone) && (
                        <div className="flex flex-col mt-1">
                          <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider mb-0.5">
                            {t('admin.providers.suggester-contact')}
                          </span>
                          {p.contact.email && (
                            <span className="text-xs font-bold text-amber-900 truncate">{p.contact.email}</span>
                          )}
                          {p.contact.phone && (
                            <span className="text-xs text-amber-700">{p.contact.phone}</span>
                          )}
                        </div>
                      )}
                      <div className="mt-2 flex items-start gap-1.5 p-1.5 rounded bg-amber-100/80 border border-amber-300/60">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
                        <span className="text-[10px] leading-tight text-amber-800 font-medium">
                          {t('admin.providers.anonymous-warning')}
                        </span>
                      </div>
                    </div>
                  )}
                  <p className="text-gray-500 text-sm line-clamp-2 mb-4 h-10">{p.details?.description || t('admin.no-description')}</p>
        
                  <div className="space-y-3 mb-4 border-t border-gray-50 pt-4 flex-1">
                    {summary && (
                      <div className="flex items-start gap-2">
                        <div className="w-[18px] h-[18px] rounded-[5px] bg-emerald-50 flex items-center justify-center shrink-0 mt-0.5"><ClockIcon className="w-[11px] h-[11px] text-emerald-500" /></div>
                        <HoursPreview summary={summary} perDay={perDay} />
                      </div>
                    )}
                    {p.location?.address && (
                      <div className="flex items-start gap-2 text-xs text-gray-600">
                        <MapPinIcon className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                        <span className="leading-tight">{p.location.address}</span>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-x-3 gap-y-2 pt-1 border-t border-gray-50 mt-2">
                      {p.contact?.email && (
                        <div className="flex items-center gap-1.5 text-[11px] text-gray-500 bg-gray-50 px-2 py-1 rounded-md border border-gray-100 max-w-full">
                          <EnvelopeIcon className="h-3 w-3 shrink-0 text-blue-400" />
                          <a href={`mailto:${p.contact.email}`} className="truncate hover:text-blue-600">{p.contact.email}</a>
                        </div>
                      )}
                      {p.contact?.phone && (
                        <div className="flex items-center gap-1.5 text-[11px] text-gray-500 bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
                          <PhoneIcon className="h-3 w-3 shrink-0 text-green-400" />
                          <a href={`tel:${p.contact.phone}`} className="hover:text-green-600">{p.contact.phone}</a>
                        </div>
                      )}
                      {p.details?.website && (
                        <div className="flex items-center gap-1.5 text-[11px] text-gray-500 bg-gray-50 px-2 py-1 rounded-md border border-gray-100 max-w-full">
                          <GlobeAltIcon className="h-3 w-3 shrink-0 text-purple-400" />
                          <a href={/^https?:\/\//i.test(p.details.website) ? p.details.website : `https://${p.details.website}`} target="_blank" rel="noopener noreferrer" className="truncate hover:text-purple-600">
                            {t('map.visit-website')}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                  <>
                   <div className="mt-4 border-t border-gray-100 pt-4">
                        <details className="group border border-gray-200 rounded-lg bg-white overflow-hidden transition-all duration-300">
                          <summary className="list-none flex items-center justify-between p-3 cursor-pointer hover:bg-indigo-50/30 transition-colors">
                            <div className="flex items-center gap-2">
                              <div className="p-1 rounded bg-indigo-50">
                                <CalendarIcon className="h-3.5 w-3.5 text-indigo-500" />
                              </div>
                              <span className="text-[11px] font-bold text-indigo-900 uppercase tracking-tight">
                                {t('admin.tabs.events')}
                              </span>
                              <span className="bg-indigo-600 text-white px-2 py-0.5 rounded-full text-[10px] font-bold">
                                {p.events?.length || 0}
                              </span>
                            </div>
                            {/* This arrow rotates when the dropdown is open */}
                            <ChevronDownIcon className="h-4 w-4 text-gray-400 group-open:rotate-180 transition-transform duration-200" />
                          </summary>
                          
                          <div className="p-2 space-y-2 bg-gray-50/50 border-t border-gray-100 max-h-48 overflow-y-auto">
                            {p.events && p.events.length > 0 ? (
                              p.events.map((event) => (
                                <div key={event._id} className="flex flex-col p-2 bg-white rounded border border-gray-200 shadow-sm">
                                  <div className="flex justify-between items-start">
                                    <span className="text-xs font-bold text-gray-800 truncate pr-2">
                                      {event.title}
                                    </span>
                                    <span className="text-[9px] font-medium bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded uppercase">
                                      {event.category}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-1 text-[10px] text-gray-500">
                                    <ClockIcon className="h-3 w-3" />
                                    <span>{formatEventDate(event.start, event.end)}</span>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="py-4 text-center">
                                <p className="text-[11px] text-gray-400 italic font-medium">{t('admin.no-events')}</p>
                              </div>
                            )}
                          </div>
                        </details>
                      </div>
                 </>
                              
                  <div className="flex gap-2 border-t pt-4 mt-auto">
                    {viewMode === 'pending' ? (
                      <>
                        <button onClick={() => handleApprove(p._id)} className="flex-1 bg-[#1d5cab] text-white py-2 rounded-lg text-sm font-bold">{t('general.approve')}</button>
                        <button onClick={() => handleReject(p._id)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-bold">{t('general.reject')}</button>
                      </>
                    ) : (
                      <>
                        <Link to={`/admin/edit-listing/${p._id}`} className="flex-1 bg-gray-50 text-[#1d5cab] text-center py-2 rounded-lg text-sm font-bold border border-blue-50">{t('general.edit')}</Link>
                        <button onClick={() => handleDeleteProvider(p._id)} className="flex-1 bg-white text-red-600 border border-red-100 py-2 rounded-lg text-sm font-bold">{t('general.delete')}</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* EVENTS TAB */}
          {activeTab === 'events' && filteredList.map(e => (
            <div key={e._id} className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow flex flex-col relative overflow-hidden">
                <div className="h-40 w-full bg-gray-50 relative border-b border-gray-100">
                    {e.photoUrl ? <img src={e.photoUrl} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><PhotoIcon className="h-12 w-12 opacity-20" /></div>}
                    <div className="absolute top-3 left-3 z-10">
                      <button  onClick={(setEvent) => handleToggleStar(e._id, e.isStarred)} className={`p-2 rounded-full shadow-lg transition-all duration-200 backdrop-blur-md ${ e.isStarred ? 'bg-yellow-400 text-white scale-110 ring-2 ring-white' : 'bg-black/20 text-white hover:bg-black/40' }`}
                          title={e.isStarred ? t('admin.unstar-event') : t('admin.star-event')} >
                          <StarIcon className={`h-4 w-4 ${e.isStarred ? 'fill-current' : ''}`} />
                      </button>
                  </div>
                    <div className="absolute top-3 right-3 flex flex-col items-end gap-2">
                    <span className="text-[10px] font-bold uppercase px-2 py-1 rounded shadow-sm" style={{ backgroundColor: getCategoryColor(e.category).light, color: getCategoryColor(e.category).primary }}>
                          {t(`events.event-management.event-type.${normalizeKey(e.category)}`, { defaultValue: e.category })}
                    </span>
                        <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded shadow-sm border ${e.status === 'pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-green-50 text-green-700 border-green-200'}`}>{t(`admin.status.${e.status}`)}</span>
                    </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-bold text-lg text-gray-900 mb-3 leading-tight">{e.title}</h3>
                    <div className="flex items-center gap-2 mb-4 group/provider">
                        <div className="p-1 bg-blue-50 rounded"><BuildingStorefrontIcon className="h-3 w-3 text-blue-600" /> </div>
                        <div className="text-[11px] truncate"> <span className="text-gray-400 mr-1">{t('events.hosted-by')}:</span>
                          <Link to={`/admin/providers/${e.provider?._id}`} className="font-bold text-gray-700 hover:text-blue-600 transition-colors">
                            {e.provider?.businessName || t('admin.unknown-provider')} </Link>
                        </div>
                      </div>
                    <div className="space-y-3 mb-4 flex-grow">
                        <div className="flex items-start gap-2">
                            <div className="w-5 h-5 rounded bg-blue-50 flex items-center justify-center shrink-0 mt-0.5"><CalendarIcon className="h-3 w-3 text-blue-600" /></div>
                            <div className="text-[11px] leading-tight">
                                <span className="block font-bold text-gray-400 uppercase tracking-tighter">{t('events.start')}</span>
                                <span className="text-gray-700 font-medium">{formatEventDate(e.start, e.end)}</span>
                            </div>
                        </div>
                        <div className="flex items-start gap-2 pt-2 border-t border-gray-50">
                            <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                            <p className="text-[11px] font-medium text-gray-600 leading-tight">{e.provider?.location?.address || e.provider?.businessName || t('events.unknown-venue')}</p>
                        </div>
                    </div>
                    <div className="border-t border-gray-100 pt-4 mt-auto flex gap-2">
                        {e.status === 'pending' ? (
                            <>
                                <button onClick={() => handleApproveEvent(e._id)} className="flex-1 bg-[#1d5cab] text-white py-2 rounded-lg text-sm font-bold">{t('general.approve')}</button>
                                <button onClick={() => handleDeleteEvent(e._id)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-bold">{t('general.reject')}</button>
                            </>
                        ) : (
                            <>
                                <button onClick={() => handleEditEventClick(e)} className="flex-1 bg-gray-50 text-[#1d5cab] text-center py-2 rounded-lg text-sm font-bold border border-blue-50">{t('general.edit')}</button>
                                <button onClick={() => handleDeleteEvent(e._id)} className="flex-1 bg-white text-red-600 border border-red-100 py-2 rounded-lg text-sm font-bold">{t('general.delete')}</button>
                            </>
                        )}
                    </div>
                </div>
            </div>
          ))}

          {/* USERS TAB */}
          {activeTab === 'users' && filteredList.map(u => (
            <div key={u._id} className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col hover:shadow-md transition-shadow overflow-hidden">
                <div className="p-5 border-b border-gray-50 bg-slate-50/50">
                    <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-500 flex items-center justify-center text-white font-bold text-lg shadow-inner">{u.name?.charAt(0) || u.email?.charAt(0)}</div>
                            <div>
                                <h3 className="font-bold text-gray-900 leading-tight flex items-center gap-1">
                                    {u.name}
                                    {u.role === 'admin' && <ShieldCheckIcon className="h-4 w-4 text-amber-500" />}
                                </h3>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-700 uppercase tracking-wider">{u.role || 'user'}</span>
                            </div>
                        </div>
                        <div className="flex flex-col items-end">
                            {u.isVerified ? (
                              <div className="flex items-center gap-1 text-emerald-600 text-[10px] font-bold uppercase"><CheckBadgeIcon className="h-4 w-4" /> {t('admin.users.verified')}</div>
                            ) : (
                              <div className="flex items-center gap-1 text-gray-400 text-[10px] font-bold uppercase"><NoSymbolIcon className="h-4 w-4" /> {t('admin.users.unverified')}</div>
                            )}
                            <span className="text-[9px] text-gray-400 mt-1">Joined {new Date(u.createdAt).toLocaleDateString()}</span>
                        </div>
                    </div>
                </div>
                <div className="p-5 space-y-4 flex-1">
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs">
                            <EnvelopeIcon className="h-4 w-4 text-gray-400 shrink-0" />
                            <a href={`mailto:${u.email}`} className="text-blue-600 font-medium truncate">{u.email}</a>
                        </div>
                        {u.defaultLocation?.address && (
                          <div className="flex items-start gap-2 text-xs text-gray-600">
                            <MapPinIcon className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                            <span className="leading-tight italic">{u.defaultLocation.address}</span>
                          </div>
                        )}
                    </div>

                    {/* PROVIDER LISTINGS (Offerings) */}
                    {u.role === 'provider' && (
                      <div className="pt-3 border-t border-gray-100">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                          {t('admin.users.business-listings')}
                        </p>
                        {/* This assumes your API populates providers linked to this user */}
                        {u.ownedProviders && u.ownedProviders.length > 0 ? (
                          <div className="space-y-2">
                            {u.ownedProviders.map(p => (
                              <div key={p._id} className="flex items-center justify-between p-2 rounded-lg bg-blue-50/50 border border-blue-100">
                                <div className="flex items-center gap-2 overflow-hidden">
                                  <BuildingStorefrontIcon className="h-3 w-3 text-blue-500 shrink-0" />
                                  <span className="text-[11px] font-bold text-blue-900 truncate">{p.businessName}</span>
                                </div>
                                <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                                  p.contractStatus === 'Active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {p.contractStatus}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-[10px] italic text-gray-400">{t('admin.users.no-listings-found')}</p>
                        )}
                      </div>
                    )}
                      
                    <div className="grid grid-cols-2 gap-2 pt-2">
                        <div className="bg-pink-50 rounded-lg p-2 flex items-center gap-2 border border-pink-100">
                            <HeartIcon className="h-4 w-4 text-pink-500" />
                            <div className="flex flex-col">
                                <span className="text-[10px] font-bold text-pink-700 uppercase">{t('admin.users.favorites')}</span>
                                <span className="text-xs font-bold text-pink-900">{u.favorites?.length || 0}</span>
                            </div>
                        </div>
                        <div className="bg-indigo-50 rounded-lg p-2 flex items-center gap-2 border border-indigo-100">
                            <CalendarDaysIcon className="h-4 w-4 text-indigo-500" />
                            <div className="flex flex-col">
                                <span className="text-[10px] font-bold text-indigo-700 uppercase">{t('admin.users.events')}</span>
                                <span className="text-xs font-bold text-indigo-900">{u.savedEvents?.length || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="p-4 bg-gray-50 border-t border-gray-100 flex gap-2">
                    <Link to={`/admin/users/${u._id}`} className="flex-1 bg-white border border-gray-200 text-gray-700 text-center py-2 rounded-lg text-xs font-bold hover:bg-gray-100 transition-colors">{t('admin.view-profile')}</Link>
                    <button onClick={() => handleDeleteUser(u._id)} className="px-3 bg-white text-red-500 border border-red-100 py-2 rounded-lg hover:bg-red-50"><TrashIcon className="h-4 w-4" /></button>
                </div>
            </div>
          ))}

        </div>
      )}

      {!loading && filteredList.length === 0 && (
        <div className="text-center py-20 text-gray-400 flex flex-col items-center">
          <MagnifyingGlassIcon className="h-12 w-12 mb-2 opacity-10" />
          <p className="text-lg font-medium">{t('admin.no-results')}</p>
        </div>
      )}

      {editingEvent && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="h-16 flex justify-between items-center px-6 bg-blue-600 rounded-t-2xl text-white">
              <h2 className="text-xl font-bold">{t('general.edit')}</h2>
              <button onClick={() => setEditingEvent(null)}><XMarkIcon className="h-6 w-6" /></button>
            </div>
            <form onSubmit={handleEditEventSubmit} className="p-6 space-y-4">
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-400 mb-1 block">{t('events.event-management.event-title')}</label>
                <input className="w-full border border-gray-300 p-2 rounded-lg" value={editFormData.title} onChange={e => setEditFormData({...editFormData, title: e.target.value})} required />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-400 mb-1 block">{t('events.event-management.description')}</label>
                <textarea rows="4" className="w-full border border-slate-300 p-2 rounded-lg resize-none text-sm" value={editFormData.description} onChange={e => setEditFormData({...editFormData, description: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] font-bold uppercase text-slate-400 mb-1 block">{t('events.start')}</label>
                  <input type="datetime-local" step="60" className="w-full border border-gray-300 p-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={editFormData.start}
                    onChange={e => {
                      const newStart = e.target.value;
                      setEditFormData(prev => {
                        const updated = { ...prev, start: newStart };
                        if (!editEndUserModified && newStart) {
                          const endDate = new Date(newStart);
                          endDate.setMinutes(endDate.getMinutes() + 60);
                          updated.end = toDateTimeLocal(endDate);
                        }
                        return updated;
                      });
                      setEditEndError('');
                    }} required />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase text-slate-400 mb-1 block">{t('events.end')}</label>
                  <input type="datetime-local" step="60"
                    className={`border p-2 rounded-lg w-full text-sm outline-none ${editEndError ? 'border-red-400 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-500'} focus:ring-2`}
                    value={editFormData.end}
                    onChange={e => {
                      setEditEndUserModified(true);
                      setEditFormData({...editFormData, end: e.target.value});
                      setEditEndError('');
                    }} required />
                  {editEndError && <p className="text-xs text-red-500 mt-1 font-semibold">{editEndError}</p>}
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-400 mb-1 block">{t('events.event-management.event-type.title')}</label>
                <select className="w-full border border-slate-300 p-2 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" value={editFormData.category} onChange={e => setEditFormData({...editFormData, category: e.target.value})}>
                  <option value="Sale">{t('events.event-management.event-type.sale')}</option>
                  <option value="Workshop">{t('events.event-management.event-type.workshop')}</option>
                  <option value="Community">{t('events.event-management.event-type.community')}</option>
                  <option value="Music">{t('events.event-management.event-type.music')}</option>
                  <option value="Food">{t('events.event-management.event-type.food')}</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-400 mb-1 block">{t('events.event-management.recurrence.label')}</label>
                <select className="w-full border border-slate-300 p-2 rounded-lg" value={editFormData.recurrenceType} onChange={e => setEditFormData({...editFormData, recurrenceType: e.target.value})}>
                  <option value="none">{t('events.event-management.recurrence.none')}</option>
                  <option value="weekly">{t('events.event-management.recurrence.weekly')}</option>
                  <option value="monthly">{t('events.event-management.recurrence.monthly')}</option>
                </select>
                {editFormData.recurrenceType !== 'none' && (
                  <div className="mt-2">
                    <label className="text-[11px] font-bold uppercase text-slate-400 mb-1 block">{t('events.event-management.recurrence.end-date')}</label>
                    <input type="date" className="w-full border border-slate-300 p-2 rounded-lg text-sm" value={editFormData.recurrenceEndDate} onChange={e => setEditFormData({...editFormData, recurrenceEndDate: e.target.value})} />
                  </div>
                )}
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-400 mb-1 block">{t('events.event-management.location.title')}</label>
                <div className="flex gap-3 mb-2">
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="radio" name="editAdminLocType" checked={editFormData.locationUseProvider} onChange={() => setEditFormData({...editFormData, locationUseProvider: true})} />
                    {t('events.event-management.location.use-provider')}
                  </label>
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="radio" name="editAdminLocType" checked={!editFormData.locationUseProvider} onChange={() => setEditFormData({...editFormData, locationUseProvider: false})} />
                    {t('events.event-management.location.custom')}
                  </label>
                </div>
                {!editFormData.locationUseProvider && (
                  <div className="space-y-2">
                    <input className="w-full border border-slate-300 p-2 rounded-lg text-sm" placeholder={t('events.event-management.location.name')} value={editFormData.locationName} onChange={e => setEditFormData({...editFormData, locationName: e.target.value})} />
                    <input className="w-full border border-slate-300 p-2 rounded-lg text-sm" placeholder={t('events.event-management.location.address')} value={editFormData.locationAddress} onChange={e => setEditFormData({...editFormData, locationAddress: e.target.value})} />
                    <button type="button" onClick={() => setEditShowMapPicker(p => !p)} className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors">
                      <span>{editShowMapPicker ? '▲' : '▼'}</span>
                      {t('events.event-management.location.pick-on-map') || 'Pick on map'}
                      {editFormData.locationLat && <span className="ml-1 text-green-600">✓ pinned</span>}
                    </button>
                    {editShowMapPicker && (
                      <LocationMapPicker
                        position={editFormData.locationLat ? [Number(editFormData.locationLat), Number(editFormData.locationLng)] : null}
                        onPick={({ lat, lng, address }) => setEditFormData(prev => ({ ...prev, locationLat: lat, locationLng: lng, locationAddress: address || prev.locationAddress }))}
                        onClear={() => setEditFormData(prev => ({ ...prev, locationLat: '', locationLng: '' }))}
                      />
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-slate-400 mb-1 block">{t('events.event-image') || 'Event Image'}</label>
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:bg-slate-50 transition-colors">
                  {editImagePreview ? (
                    <div className="relative">
                      <img src={editImagePreview} alt="Preview" className="max-h-32 mx-auto rounded-lg" />
                      <button type="button" onClick={() => { setEditImagePreview(null); setEditSelectedImage(null); }} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 shadow-sm hover:bg-red-600">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <PhotoIcon className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                      <span className="text-xs text-slate-400 block">{t('events.upload-image')}</span>
                      <span className="text-[10px] text-slate-300 block mt-0.5">{t('events.image-hint')}</span>
                      <input type="file" className="hidden" accept=".jpg,.jpeg,.png" onChange={handleEditImageChange} />
                    </label>
                  )}
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setEditingEvent(null)} className="flex-1 border border-gray-300 py-2 rounded-lg text-sm">{t('general.cancel')}</button>
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-bold">{t('general.save')}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ProviderManager;
